import { useRef } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import useIframeParams from '../../../hooks/useIframeParams';

/**
 * Iframe loaded via POST to `/auth/authorize` (JWT / OIDC implicit-style flow).
 * Payload fields come from tab `iframeParams`.
 *
 * @param {string} [lmsObjectId] - First argument to useLmsAction (default: window.location.href).
 * @param {string} [lmsObjectName] - LMS object name for useLmsAction.
 * @param {boolean} [lmsAppLevel=false] - Third argument to useLmsAction.
 * @param {string} [iframeTitle] - Accessible iframe title
 * @param {string} [allowOptions] - iframe allow attribute (default: "").
 * @param {string} [providerIframeName] - Provider key for config lookup (window.config.jwtIframeProviders[providerIframeName]).
 * @param {string[]|object} [iframeParams] - Fields to send in OAuth state.
 * @param {string} [redirect_uri] - OAuth redirect_uri from: `${window.location.origin}${window.config.iframeJwtOAuthRedirectPath}`.
 */
const JwtAuthIframe = ({ providerIframeName, client_id, providerLink, lmsObjectId, lmsObjectName, lmsAppLevel = false, iframeTitle, allowOptions, iframeParams } = {}) => {

    const iframeRef = useRef(null);
    const userDetails = useSelector(state => state.auth.userDetails);
    const { lang } = useSelector(state => state.translate);
    const payload = useIframeParams(iframeParams);
    const payloadKey = JSON.stringify(payload);

    //check
    // useLmsAction(
    //     lmsObjectId ?? window.location.href,
    //     lmsObjectName,
    //     lmsAppLevel
    // );

    useEffect(() => {
        const loadIframeContent = () => {
            if (iframeRef.current) {
                // Dynamically create the form within the iframe
                const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
                const form = iframeDoc.createElement("form");
                form.method = "POST";
                form.action = `${window.config.baseApiUrl}/auth/authorize`;
                const providersConfig = window.config.jwtIframeProviders[providerIframeName];
                const params = {
                    response_type: "id_token",
                    client_id: providersConfig?.client_id || client_id,
                    redirect_uri: `${window.location.origin}${window.config.iframeJwtOAuthRedirectPath}`,
                    scope: "profile",
                    nonce: "random_nonce",
                    prompt: "none",
                    state: JSON.stringify({
                        providerLink: providersConfig?.providerLink || providerLink,
                        ...payload
                    }),
                };
                for (const [key, value] of Object.entries(params)) {
                    const input = iframeDoc.createElement("input");
                    input.type = "hidden";
                    input.name = key;
                    input.value = value;
                    form.appendChild(input);
                }
                // Append the form to the iframe's body and submit it
                iframeDoc.body.appendChild(form);
                form.submit(); // Redirect happens within the iframe
            }
        };
        if (userDetails)
            loadIframeContent();
        // eslint-disable-next-line
    }, [userDetails, payloadKey]);

    //check if need
    useEffect(() => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                { action: "langChange", lang: lang }, '*'
            );
        }
    }, [lang]);

    return (
        <div>
            <iframe
                ref={iframeRef}
                frameBorder={0}
                title={iframeTitle}
                style={{ width: "100%", height: "100%", position: "absolute" }}
                allow={allowOptions || ""}
            ></iframe>
        </div>
    );
};

export default JwtAuthIframe;
